import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BellOff, Check, FileCheck, LocateFixed, Waypoints } from 'lucide-react';
import { ESTADOS_ALERTA, NIVELES_ALERTA, TIPOS_ALERTA, TITULO_POR_TIPO, type EstadoAlerta, type NivelAlerta, type TipoAlerta } from '@tms/contratos';
import { ChipEstado } from '../components/ChipEstado';
import { useAccionesAlerta } from '../components/DialogoJustificar';
import { MarcaEscalada, describirAlerta, troncalARutear } from '../components/FilaAlerta';
import { ALERTA, ICONO_TIPO_ALERTA, NIVEL } from '../components/estados';
import { compararPorNivel } from '../util/alertas';
import { useDatos } from '../state/Datos';
import { diaDe, fmtFechaHora, fmtHace } from '../util/format';
import { useAhora } from '../util/hooks';

export function AlertasPagina() {
  const navegar = useNavigate();
  const { alertas } = useDatos();
  const { resolver, pedirJustificacion, dialogo } = useAccionesAlerta();
  const ahora = useAhora(30_000);
  const [estado, setEstado] = useState<'' | EstadoAlerta>('ABIERTA');
  const [tipo, setTipo] = useState<'' | TipoAlerta>('');
  const [fecha, setFecha] = useState('');
  const [nivel, setNivel] = useState<'' | NivelAlerta>('');
  const [orden, setOrden] = useState<'nivel' | 'recientes'>('nivel');

  const filtradas = useMemo(
    () =>
      alertas
        .filter((a) => (!estado || a.estado === estado) && (!tipo || a.tipo === tipo) && (!nivel || a.nivel === nivel) && (!fecha || diaDe(a.inicio) === fecha))
        .sort(orden === 'nivel' ? compararPorNivel : (a, b) => b.inicio.localeCompare(a.inicio)),
    [alertas, estado, tipo, nivel, fecha, orden],
  );

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">OPERACIONES / NOVEDADES</div>
          <h1>Alertas</h1>
        </div>
        <span className="list-count">{filtradas.length} de {alertas.length}</span>
      </div>

      <div className="toolbar">
        <label className="field">
          <span>Estado</span>
          <select value={estado} onChange={(e) => setEstado(e.target.value as '' | EstadoAlerta)}>
            <option value="">Todos</option>
            {ESTADOS_ALERTA.map((e) => (
              <option key={e} value={e}>
                {ALERTA[e].etiqueta}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Nivel</span>
          <select value={nivel} onChange={(e) => setNivel(e.target.value as '' | NivelAlerta)}>
            <option value="">Todos los niveles</option>
            {[...NIVELES_ALERTA].reverse().map((n) => (
              <option key={n} value={n}>
                {NIVEL[n].etiqueta}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Tipo</span>
          <select value={tipo} onChange={(e) => setTipo(e.target.value as '' | TipoAlerta)}>
            <option value="">Todos los tipos</option>
            {TIPOS_ALERTA.map((t) => (
              <option key={t} value={t}>
                {TITULO_POR_TIPO[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Fecha de inicio</span>
          <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
        </label>
        <label className="field">
          <span>Orden</span>
          <select value={orden} onChange={(e) => setOrden(e.target.value as 'nivel' | 'recientes')}>
            <option value="nivel">Por nivel (críticas primero)</option>
            <option value="recientes">Más recientes primero</option>
          </select>
        </label>
        <button type="button" className="text-button" onClick={() => { setEstado(''); setTipo(''); setNivel(''); setFecha(''); }}>
          Limpiar filtros
        </button>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="tabla">
            <thead>
              <tr>
                <th>Novedad</th>
                <th>Nivel</th>
                <th>Unidad</th>
                <th>Viaje</th>
                <th>Inicio</th>
                <th>Estado</th>
                <th>Detalle</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map((a) => {
                const Icono = ICONO_TIPO_ALERTA[a.tipo];
                return (
                  <tr key={a.id}>
                    <td>
                      <strong className={NIVEL[a.nivel].color} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        <Icono aria-hidden size={15} />
                        {a.titulo}
                      </strong>
                      {a.ubicacionNombre && <small>{a.ubicacionNombre}</small>}
                    </td>
                    <td>
                      <span className="celda-nivel">
                        <ChipEstado tipo="nivel" valor={a.nivel} />
                        <MarcaEscalada alerta={a} />
                      </span>
                    </td>
                    <td>
                      {a.patente ?? a.vehiculoIdMicrotrack ?? '—'}
                      <small>{a.choferNombre ?? ''}</small>
                    </td>
                    <td>{a.viajeId ? <Link to={`/viajes/${a.viajeId}`}>{a.viajeIdMara}</Link> : '—'}</td>
                    <td>
                      {fmtFechaHora(a.inicio)}
                      <small>{fmtHace(a.inicio, ahora)}</small>
                    </td>
                    <td>
                      <ChipEstado tipo="alerta" valor={a.estado} />
                      {a.estado !== 'ABIERTA' && <small>{a.cerradaAutomaticamente ? 'Automática' : a.cambiadoPor?.nombre}</small>}
                    </td>
                    <td style={{ maxWidth: 340 }}>
                      {describirAlerta(a)}
                      {a.motivo && <small>{a.motivo}</small>}
                    </td>
                    <td>
                      {a.estado === 'ABIERTA' ? (
                        <div className="alert-actions" style={{ marginTop: 0 }}>
                          {troncalARutear(a) !== null && (
                            <Link className="boton-link" to={`/troncales/${troncalARutear(a)}`}>
                              <Waypoints aria-hidden />
                              Rutear troncal
                            </Link>
                          )}
                          {a.vehiculoIdMicrotrack && (
                            <button type="button" onClick={() => navegar(`/?unidad=${encodeURIComponent(a.vehiculoIdMicrotrack!)}`)}>
                              <LocateFixed aria-hidden />
                              Ver en mapa
                            </button>
                          )}
                          <button type="button" className="res" onClick={() => void resolver(a)}>
                            <Check aria-hidden />
                            Resolver
                          </button>
                          <button type="button" onClick={() => pedirJustificacion(a)}>
                            <FileCheck aria-hidden />
                            Justificar
                          </button>
                        </div>
                      ) : (
                        <span className="muted">{fmtFechaHora(a.cambiadoEn)}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtradas.length === 0 && (
          <div className="vacio">
            <BellOff aria-hidden />
            No hay alertas con esos filtros.
          </div>
        )}
      </div>
      {dialogo}
    </div>
  );
}
